import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Dashboard from './pages/Dashboard';
import Stores from './pages/Stores';
import Products from './pages/Products';
import Promotions from './pages/Promotions';
import Transactions from './pages/Transactions';
import Analytics from './pages/Analytics';
import Reports from './pages/Reports';
import Users from './pages/Users';
import Settings from './pages/Settings';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import CustomerHome from './pages/CustomerHome';
import ForgotPassword from './pages/ForgotPassword';
import LandingPage from './pages/LandingPage';
import DeleteAccount from './pages/DeleteAccount';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import Logs from './pages/Logs';
import Support from './pages/Support';
import ErrorPage from './pages/ErrorPage';
import { subscribeToChatEvents } from './utils/chatEvents';
import { getApiBase } from './utils/apiBase';
import './App.css';

// ── Paths that render without the sidebar/header shell ──────────
const PUBLIC_PATHS = ['/', '/login', '/forgot-password', '/reset-password', '/delete-account', '/privacy', '/terms', '/support'];

// ── Default landing page per role after login ────────────────────
const ROLE_DEFAULT_PAGE: Record<string, string> = {
  admin:    'dashboard',
  manager:  'dashboard',
  staff:    'dashboard',
  customer: 'home',      // handled separately — renders CustomerHome
  dev:      'logs',      // devs land on Logs immediately
};

const AppInner: React.FC = () => {
  const navigate  = useNavigate();
  const location  = useLocation();

  const storedUser = localStorage.getItem('authUser');
  const user       = storedUser ? JSON.parse(storedUser) : null;
  const role       = user?.role || '';

  const [currentPage, setCurrentPage]         = useState<string>(ROLE_DEFAULT_PAGE[role] || 'dashboard');
  const [searchTerm, setSearchTerm]           = useState<string>('');
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(!!localStorage.getItem('authToken'));
  const [totalUnread, setTotalUnread]         = useState<number>(0);
  const API_BASE                              = getApiBase();

  const roleAccess: { [key: string]: string[] } = {
    admin:   ['dashboard', 'stores', 'products', 'promotions', 'transactions', 'analytics', 'reports', 'users', 'logs', 'settings'],
    manager: ['dashboard', 'stores', 'products', 'promotions', 'transactions', 'analytics'],
    staff:   ['dashboard', 'products', 'transactions'],
    customer:[],
    dev:     ['users', 'logs'],
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    setIsAuthenticated(false);
    navigate('/');
  };

  const logoutTimeout = useRef<NodeJS.Timeout | null>(null);

  const resetLogoutTimer = () => {
    if (logoutTimeout.current) clearTimeout(logoutTimeout.current);
    logoutTimeout.current = setTimeout(() => {
      alert('You have been logged out due to inactivity.');
      logout();
    }, 20 * 60 * 1000);
  };

  useEffect(() => {
    if (isAuthenticated) {
      resetLogoutTimer();
      const events = ['mousemove', 'keydown', 'mousedown', 'touchstart'];
      events.forEach((e) => window.addEventListener(e, resetLogoutTimer));
      return () => {
        events.forEach((e) => window.removeEventListener(e, resetLogoutTimer));
        if (logoutTimeout.current) clearTimeout(logoutTimeout.current);
      };
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const fetchUnreadCounts = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/chat/conversations`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        const conversations = data.data || [];
        const total = conversations.reduce(
          (acc: number, conv: any) => acc + (conv.unreadCount || 0),
          0
        );
        setTotalUnread(total);
      } catch (err) {
        console.error(err);
      }
    };

    // Devs and customers don't need unread chat counts
    if (isAuthenticated && role !== 'customer' && role !== 'dev') {
      fetchUnreadCounts();
      const subscription = subscribeToChatEvents(
        API_BASE,
        token,
        event => {
          if (event.type === 'chat.unread.changed') {
            setTotalUnread(Number(event.data?.unreadCount || 0));
          }
          if (event.type === 'chat.message.created') {
            fetchUnreadCounts();
          }
        }
      );
      return () => subscription.close();
    }
  }, [API_BASE, isAuthenticated, role]);

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
    // Re-read user from storage (set by Login page before calling this callback)
    const freshUser = localStorage.getItem('authUser');
    const freshRole = freshUser ? JSON.parse(freshUser).role : '';
    const defaultPage = ROLE_DEFAULT_PAGE[freshRole] || 'dashboard';
    setCurrentPage(defaultPage);
    resetLogoutTimer();
    navigate('/dashboard');
  };

  const isPublicPath  = PUBLIC_PATHS.includes(location.pathname);
  const isShellHidden = isPublicPath || !isAuthenticated || role === 'customer';

  const getPageTitle = (): string => {
    const titles: { [key: string]: string } = {
      dashboard:    'Dashboard Overview',
      stores:       'Stores Management',
      products:     'Products Catalog',
      promotions:   'Promotions',
      transactions: 'Transaction History',
      analytics:    'Sales Analytics',
      reports:      'Reports',
      users:        'User Management',
      logs:         'API Logs',
      settings:     'Settings',
    };
    return titles[currentPage] || 'Dashboard';
  };

  const renderDashboardPage = (): React.ReactElement => {
    if (role === 'customer') return <CustomerHome onLogout={logout} />;

    const allowedPages = roleAccess[role] || [];
    if (!allowedPages.includes(currentPage)) {
      return (
        <ErrorPage
          kind="403"
          inDashboard
          onBack={() => window.history.back()}
          onHome={() => setCurrentPage(ROLE_DEFAULT_PAGE[role] || 'dashboard')}
        />
      );
    }

    switch (currentPage) {
      case 'dashboard':    return <Dashboard setCurrentPage={setCurrentPage} />;
      case 'stores':       return <Stores />;
      case 'products':     return <Products searchTerm={searchTerm} setSearchTerm={setSearchTerm} />;
      case 'promotions':   return <Promotions />;
      case 'transactions': return <Transactions />;
      case 'analytics':    return <Analytics />;
      case 'reports':      return <Reports />;
      case 'users':        return <Users />;
      case 'logs':         return <Logs />;
      case 'settings':     return <Settings />;
      default:             return <Logs />; // dev fallback
    }
  };

  const landingProps = {
    onLoginClick:          () => navigate('/login'),
    onDeleteAccountClick:  () => navigate('/delete-account'),
    onForgotPasswordClick: () => navigate('/forgot-password'),
  };

  return (
    <div className="min-h-screen flex bg-gray-50">
      {!isShellHidden && (
        <Sidebar
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalUnread={totalUnread}
        />
      )}

      <div className="flex-1 flex flex-col">
        {!isShellHidden && <Header title={getPageTitle()} />}

        <main className={!isShellHidden ? 'flex-1 p-6 overflow-y-auto bg-gray-50' : 'flex-1'}>
          <Routes>
            {/* ── Public routes ── */}
            <Route path="/"                element={<LandingPage {...landingProps} />} />
            <Route path="/login"           element={<Login onSuccess={handleLoginSuccess} />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password"  element={<ResetPassword />} />
            <Route path="/privacy"         element={<PrivacyPage />} />
            <Route path="/terms"           element={<TermsPage />} />
            <Route path="/support"         element={<Support />} />
            <Route
              path="/403"
              element={
                <ErrorPage
                  kind="403"
                  inDashboard={isAuthenticated && !isShellHidden}
                  onBack={() => window.history.back()}
                  onHome={() => isAuthenticated ? navigate('/dashboard') : navigate('/login')}
                />
              }
            />
            <Route
              path="/delete-account"
              element={
                <DeleteAccount
                  onCancel={() => navigate('/')}
                  onDeleted={() => navigate('/')}
                />
              }
            />

            {/* ── Protected route ── */}
            <Route
              path="/dashboard"
              element={
                isAuthenticated
                  ? renderDashboardPage()
                  : <Login onSuccess={handleLoginSuccess} />
              }
            />

            {/* ── Catch-all ── */}
            <Route
              path="*"
              element={
                isAuthenticated
                  ? (
                    <ErrorPage
                      kind="404"
                      inDashboard={!isShellHidden}
                      onBack={() => window.history.back()}
                      onHome={() => navigate('/dashboard')}
                    />
                  )
                  : (
                    <ErrorPage
                      kind="404"
                      onBack={() => window.history.back()}
                      onHome={() => navigate('/')}
                    />
                  )
              }
            />
          </Routes>
        </main>
      </div>
    </div>
  );
};

const App: React.FC = () => (
  <Router>
    <AppInner />
  </Router>
);

export default App;
